import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import {motion} from 'framer-motion';
import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

const tabs = {
    initial: {
        height: 'auto',
        x: '0px'
    },
    animate({active, tabRefs}) {
        let x = '-0px';
        let height = 'auto';

        if (tabRefs[Number(active)].current) {
            const rect = tabRefs[Number(active)].current.getBoundingClientRect();

            x = `-${active * rect.width}px`;
            height = `${rect.height}px`;
        }

        return {
            height,
            transition: {type: 'tween'},
            x
        };
    }
};

/**
 * TabContainer
 *
 * @property {*} prop Prop comment.
 *
 * @class TabContainer
 * @extends {Component}
 */
class TabContainer extends Component {
    static propTypes = {
        active: PropTypes.number.isRequired,
        position: PropTypes.string.isRequired,
        tabContents: PropTypes.array.isRequired
    }

    htmlParser = new Parser();

    /**
     * Creates an instance of TabContainer.
     *
     * @param {Object} props Component props.
     * @memberof TabContainer
     */
    constructor(props) {
        super(props);

        this.tabs = [];

        this.videoRail = React.createRef();
    }

    /**
     * Adds window listener for resize.
     *
     * @memberof TabContainer
     */
    componentDidMount() {
        window.addEventListener('resize', this.resizeActiveTabView);
    }

    /**
     * Removes window listener for resize.
     *
     * @memberof TabContainer
     */
    componentWillUnmount() {
        window.removeEventListener('resize', this.resizeActiveTabView);
    }

    /**
     * Resizes the Active Tab against viewport.
     *
     * @memberof TabContainer
     */
    @autobind
    resizeActiveTabView() {
        const {active} = this.props;
        const tabBarHeight = `${this.tabs[Number(active)].current.getBoundingClientRect().height}px`;

        this.videoRail.current.style.height = tabBarHeight;
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof TabContainer
     */
    render() {
        const {active, position, tabContents} = this.props;

        return (
            <TabContainerElement position={position}>
                <VideoRail
                    ref={this.videoRail}
                    animate="animate"
                    custom={{
                        active,
                        tabRefs: this.tabs
                    }}
                    initial="initial"
                    number={tabContents.length}
                    variants={tabs}
                >
                    {tabContents.map((contents, index) => {
                        if (!this.tabs[Number(index)]) {
                            this.tabs[Number(index)] = React.createRef();
                        }

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <ContentContainer key={index} ref={this.tabs[Number(index)]}>
                                {contents.map(item => {
                                    const {copy, video, videoPosition} = item;

                                    return (
                                        <RowContainer key={copy} position={videoPosition}>
                                            <VideoSizeWrap position={videoPosition}>
                                                <Video>
                                                    {this.htmlParser.parseWithInstructions(
                                                        video,
                                                        isValidNode,
                                                        processingInstructions
                                                    )}
                                                </Video>
                                            </VideoSizeWrap>
                                            <VideoCopyContainer>
                                                <Text>
                                                    {this.htmlParser.parseWithInstructions(
                                                        `<div data-module="VideoContent">${copy}</div>`,
                                                        isValidNode,
                                                        processingInstructions
                                                    )}
                                                </Text>
                                            </VideoCopyContainer>
                                        </RowContainer>
                                    );
                                })}
                            </ContentContainer>
                        );
                    })}
                </VideoRail>
            </TabContainerElement>
        );
    }
}

export default TabContainer;

const TabContainerElement = styled.div`
    align-self: ${({position}) => position};
    margin-left: ${({position}) => (position === 'flex-start' ? clampCss(57.594, 't') : '0')};
    margin-right: ${({position}) => (position === 'flex-end' ? clampCss(57.594, 't') : '0')};
    overflow: hidden;
    width: calc(83.33% - ${clampCss(57.594, 't')});

    ${MEDIA.DESKTOP.UP} {
        margin-left: 0;
        margin-right: 0;
        padding: 0;
        width: 83.33%;
    }
`;

/* eslint-disable @nfq/no-magic-numbers */
const VideoRail = styled(motion.div)`
    align-items: flex-start;
    display: flex;
    margin-top: ${clampCss(57, 't')};
    width: ${({number}) => `${100 * number}%`};

    ${MEDIA.DESKTOP.UP} {
        margin-top: max(57px, ${pxToVw(57, 'd')});
    }
`;
/* eslint-enable @nfq/no-magic-numbers */

const ContentContainer = styled.div`
    padding: 0 2px;
    width: 100%;
`;

const RowContainer = styled.div`
    display: flex;
    flex-direction: ${({position}) => (position === 'flex-start' ? 'row' : 'row-reverse')};
    margin-bottom: ${clampCss(32, 't')};

    &:last-child {
        margin-bottom: 0px;
    }

    ${MEDIA.DESKTOP.UP} {
        margin-bottom: max(32px, ${pxToVw(32, 'd')});
    }
`;

const VideoSizeWrap = styled.div`
    margin-left: ${({position}) => (position === 'flex-end' ? '32px' : '0')};
    margin-right: ${({position}) => (position === 'flex-start' ? '32px' : '0')};
    width: calc(60% - 16px);
`;

const Video = styled.div`
    align-items: center;
    background: black;
    border-radius: ${clampCss(8, 't')};
    display: flex;
    justify-content: center;
    overflow: hidden;
    padding-top: 56.25%;
    position: relative;
    width: 100%;

    & > iframe {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
    }

    ${MEDIA.DESKTOP.UP} {
        border-radius: max(8px, ${pxToVw(8, 'd')});
    }
`;

const VideoCopyContainer = styled.div`
    width: calc(40% - 16px);
`;

const Text = styled.div`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 't')};
    letter-spacing: ${clampCss(0.5, 't')};
    line-height: ${clampCss(16.8, 't')};
    padding-top: ${clampCss(8, 't')};

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        padding-top: max(8px, ${pxToVw(8, 'd')});
    }
`;